import React, { useEffect, useId, useRef, useState } from 'react';
import { MapPinIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { hasValidatedPlace } from '../../utils/placeDomain';
import apiService from '../../services/ApiService';
import {
  extractCityState,
  fetchGoogleMapsConfig,
  loadGoogleMaps,
} from '../../utils/googleMapsLoader';
import type { PlaceLocationValue } from '../../utils/placeValue';
import { hasPlacePin } from '../../utils/placeValue';
import { PlaceMapCard } from './PlaceMapCard';

interface PlaceLocationEditorProps {
  idPrefix: string;
  value: PlaceLocationValue;
  onChange: (value: PlaceLocationValue) => void;
  /** When true, show optional venue/display label (events). */
  showVenueLabel?: boolean;
  disabled?: boolean;
  error?: string | null;
}

/**
 * Google Places-backed location editor with optional display label.
 * Supports create and edit: existing pins load into a confirmed card with change/clear actions.
 */
export const PlaceLocationEditor: React.FC<PlaceLocationEditorProps> = ({
  idPrefix,
  value,
  onChange,
  showVenueLabel = false,
  disabled = false,
  error = null,
}) => {
  const searchId = `${idPrefix}-place-search`;
  const labelId = `${idPrefix}-venue-label`;
  const helpId = `${idPrefix}-place-help`;
  const searchRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const [mapsReady, setMapsReady] = useState(false);
  const [mapsError, setMapsError] = useState<string | null>(null);
  const hasPin = hasPlacePin(value);
  const [editing, setEditing] = useState(!hasPin);
  const listboxId = useId();

  useEffect(() => {
    setEditing(!hasPin);
  }, [hasPin]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const config = await fetchGoogleMapsConfig(async () => {
          const res = await apiService.getMapsConfig();
          return res;
        });
        if (!config.places_enabled || !config.maps_api_key) {
          if (!cancelled) {
            setMapsReady(false);
            setMapsError('Map search is not configured. Enter city and region manually.');
          }
          return;
        }
        await loadGoogleMaps(config.maps_api_key);
        if (!cancelled) {
          if (!window.google?.maps?.places) {
            setMapsReady(false);
            setMapsError(
              'Map search could not load in this browser. Enter city and region manually.',
            );
            return;
          }
          setMapsReady(true);
          setMapsError(null);
        }
      } catch {
        if (!cancelled) {
          setMapsReady(false);
          setMapsError(
            'Map search could not load in this browser. Enter city and region manually.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!mapsReady || !editing || disabled || !searchRef.current || !window.google?.maps?.places) {
      return undefined;
    }
    const autocomplete = new window.google.maps.places.Autocomplete(searchRef.current, {
      fields: ['place_id', 'formatted_address', 'geometry', 'address_components', 'name'],
      types: ['establishment', 'geocode'],
    });
    autocompleteRef.current = autocomplete;

    const listener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      const placeId = place.place_id ?? null;
      const formatted = place.formatted_address ?? null;
      const lat = place.geometry?.location?.lat();
      const lng = place.geometry?.location?.lng();
      if (!placeId || lat == null || lng == null || !formatted) {
        setMapsError('Pick a location from the list to confirm the pin.');
        return;
      }
      const { city, state } = extractCityState(place.address_components ?? undefined);
      onChange({
        ...value,
        place_id: placeId,
        formatted_address: formatted,
        latitude: lat,
        longitude: lng,
        city,
        state,
        place_cleared: false,
      });
      setMapsError(null);
      setEditing(false);
    });

    return () => {
      listener.remove();
      autocompleteRef.current = null;
    };
  }, [mapsReady, editing, disabled, onChange, value]);

  const clearPlace = () => {
    onChange({
      ...value,
      place_id: null,
      formatted_address: null,
      latitude: null,
      longitude: null,
      place_cleared: true,
    });
    setEditing(true);
    setMapsError(null);
  };

  const manualFallback = !mapsReady;

  return (
    <div className="space-y-4">
      {showVenueLabel ? (
        <div>
          <label htmlFor={labelId} className="input-label">
            Venue name <span className="text-gray-500 font-normal">(optional)</span>
          </label>
          <input
            id={labelId}
            type="text"
            className="input"
            value={value.location_label ?? ''}
            onChange={(e) => onChange({ ...value, location_label: e.target.value || null })}
            placeholder="e.g. Front Range Speedway"
            maxLength={255}
            disabled={disabled}
          />
          <p id={helpId} className="text-xs text-gray-500 mt-1">
            Shown alongside the verified address. Leave blank to use the Google Maps name only.
          </p>
        </div>
      ) : null}

      {hasPin && !editing ? (
        <div className="rounded-xl border border-white/[0.08] bg-[#0c0c0c]/60 overflow-hidden">
          <div className="flex items-start justify-between gap-3 p-4 border-b border-white/[0.06]">
            <div className="min-w-0">
              <p className="text-sm font-medium text-white flex items-center gap-2">
                <MapPinIcon className="w-4 h-4 text-orange-400 shrink-0" aria-hidden />
                Verified location
              </p>
              <p className="text-sm text-gray-300 mt-1 break-words">{value.formatted_address}</p>
              {[value.city, value.state].filter(Boolean).length > 0 ? (
                <p className="text-xs text-gray-500 mt-1">
                  {[value.city, value.state].filter(Boolean).join(', ')}
                </p>
              ) : null}
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                className="btn-ghost btn-sm"
                onClick={() => setEditing(true)}
                disabled={disabled}
              >
                Change
              </button>
              <button
                type="button"
                className="btn-ghost btn-sm inline-flex items-center gap-1 text-gray-400 hover:text-red-300"
                onClick={clearPlace}
                disabled={disabled}
                aria-label="Remove map location"
              >
                <XMarkIcon className="w-4 h-4" aria-hidden />
                Clear
              </button>
            </div>
          </div>
          <PlaceMapCard
            place={{
              location_label: value.location_label,
              place_id: value.place_id,
              formatted_address: value.formatted_address,
              latitude: value.latitude,
              longitude: value.longitude,
              city: value.city,
              state: value.state,
            }}
            compact
          />
        </div>
      ) : (
        <div>
          <label htmlFor={searchId} className="input-label">
            Search address or venue
          </label>
          {mapsReady ? (
            <input
              id={searchId}
              ref={searchRef}
              type="text"
              className="input"
              placeholder="Start typing an address or place name"
              disabled={disabled}
              autoComplete="off"
              aria-controls={listboxId}
            />
          ) : (
            <p className="text-sm text-gray-400 rounded-lg border border-white/[0.08] bg-[#0c0c0c]/40 px-3 py-2.5">
              {mapsError ?? 'Loading map search…'}
            </p>
          )}
          {manualFallback || mapsError ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <div>
                <label htmlFor={`${idPrefix}-city`} className="input-label">
                  City
                </label>
                <input
                  id={`${idPrefix}-city`}
                  type="text"
                  className="input"
                  value={value.city ?? ''}
                  onChange={(e) => onChange({ ...value, city: e.target.value || null })}
                  maxLength={100}
                  disabled={disabled}
                />
              </div>
              <div>
                <label htmlFor={`${idPrefix}-state`} className="input-label">
                  State / region
                </label>
                <input
                  id={`${idPrefix}-state`}
                  type="text"
                  className="input"
                  value={value.state ?? ''}
                  onChange={(e) => onChange({ ...value, state: e.target.value || null })}
                  maxLength={100}
                  disabled={disabled}
                />
              </div>
            </div>
          ) : null}
        </div>
      )}

      {error ? (
        <p className="text-sm text-red-300" role="alert">
          {error}
        </p>
      ) : null}
      {!hasValidatedPlace({
        place_id: value.place_id,
        latitude: value.latitude,
        longitude: value.longitude,
      }) &&
      (value.city || value.state) &&
      !hasPlacePin(value) ? (
        <p className="text-xs text-gray-500">
          Text location only. Search above to add a map and directions.
        </p>
      ) : null}
    </div>
  );
};

export default PlaceLocationEditor;
